import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import ModernCalculationSelector from "../../../utils/Selector/ModernCalculationSelector";
import {
  createOfferCalculationAsync,
  getOfferCalculationAsync,
  updateOfferCalculationAsync,
} from "../../../../slices/offer-calculation";
import { fetchOfferCalculation } from "../../../../api/offer-calculation";
import OfferTemplateWizardDialog from "../offer-template-wizard-dialog";
import NestedCalculationEditorDialog from "../calculations/nested-calculation-editor-dialog";
import CalculationNestedSourceDialog from "../calculations/calculation-nested-source-dialog";
import { templateNeedsWizard } from "../utils/template-wizard";
import { expandTemplateByUnits } from "../utils/template-expand";
import {
  buildBlankNestedCalculationPayload,
  buildNestedCopyFromTemplatePayload,
  buildReplaceFromTemplateValues,
  calculationHasReplaceableContent,
  nestedLineFromCopy,
} from "../utils/apply-calculation-template";

/**
 * Standalone calculation editor template load/use (same patterns as offer kalkyle).
 * Keeps picker + TPL wizard + nested editor out of calculation-info's create-dialog WIP.
 */
export default function useStandaloneCalculationTemplateLoad({
  formikRef,
  lockPlacement,
  unlockPlacement,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templatePickerMode, setTemplatePickerMode] = useState("replace");
  const [attachTargetRow, setAttachTargetRow] = useState(null);
  const [sourceChooserOpen, setSourceChooserOpen] = useState(false);
  const [sourceChooserBusy, setSourceChooserBusy] = useState(false);
  const [templateWizard, setTemplateWizard] = useState(null);
  const [nestedEditorId, setNestedEditorId] = useState(null);
  const [nestedEditorFocusRecommended, setNestedEditorFocusRecommended] =
    useState(false);
  const addingFromTemplateRef = useRef(false);

  const persistCalculationValues = useCallback(
    async (values) => {
      const action = values?.id
        ? updateOfferCalculationAsync(values)
        : createOfferCalculationAsync(values);
      const result = await dispatch(action);
      const rejected = values?.id
        ? updateOfferCalculationAsync.rejected.match(result)
        : createOfferCalculationAsync.rejected.match(result);
      if (rejected || result.payload?.errors) {
        toast.error(t("offers.tabs.calculation_save_failed"));
        return null;
      }
      const saved = result.payload?.data || result.payload;
      if (!saved?.id) {
        toast.error(t("offers.tabs.calculation_save_failed"));
        return null;
      }
      return saved;
    },
    [dispatch, t]
  );

  const applyPageTemplate = useCallback(
    async (calcData, parent) => {
      const expanded = expandTemplateByUnits(calcData);
      const nextValues = buildReplaceFromTemplateValues(parent, expanded);
      lockPlacement?.();
      formikRef.current?.setValues(nextValues);
      const saved = await persistCalculationValues(nextValues);
      if (!saved) {
        unlockPlacement?.();
        return;
      }
      await dispatch(getOfferCalculationAsync(saved.id));
      unlockPlacement?.();
      toast.success(t("offers.tabs.calculation_template_loaded"));
    },
    [dispatch, formikRef, lockPlacement, persistCalculationValues, t, unlockPlacement]
  );

  const attachSavedNestedLine = useCallback(
    async (savedCopy, parent, mode, targetRow) => {
      const currentItems = [
        ...(formikRef.current?.values?.items || parent.items || []),
      ];
      let nextItems;
      if (mode === "attach" && targetRow) {
        const idx = currentItems.findIndex(
          (row) =>
            row === targetRow ||
            (targetRow.row_order != null && row.row_order === targetRow.row_order)
        );
        const line = nestedLineFromCopy(
          savedCopy,
          idx >= 0 ? idx : currentItems.length,
          idx >= 0 ? currentItems[idx] : targetRow
        );
        nextItems =
          idx >= 0
            ? currentItems.map((row, i) => (i === idx ? line : row))
            : [...currentItems, line];
      } else {
        nextItems = [
          ...currentItems,
          nestedLineFromCopy(savedCopy, currentItems.length),
        ];
      }
      lockPlacement?.();
      formikRef.current?.setFieldValue("items", nextItems);
      const saved = await persistCalculationValues({
        ...(formikRef.current?.values || parent),
        items: nextItems,
      });
      if (saved?.id) {
        await dispatch(getOfferCalculationAsync(saved.id));
      }
      unlockPlacement?.();
      if (!saved?.id) {
        toast.error(t("offers.tabs.calculation_load_failed"));
        return;
      }
      toast.success(t("offers.tabs.calculation_line_added"));
      if (savedCopy?.id) {
        setNestedEditorFocusRecommended(false);
        setNestedEditorId(savedCopy.id);
      }
    },
    [dispatch, formikRef, lockPlacement, persistCalculationValues, t, unlockPlacement]
  );

  const finishNestedFromTemplate = useCallback(
    async (calcData, parent, mode, targetRow) => {
      const expanded = expandTemplateByUnits(calcData);
      const payload = buildNestedCopyFromTemplatePayload(expanded, {
        parentId: parent.id,
        offerId: parent.offer_id || null,
      });
      const result = await dispatch(createOfferCalculationAsync(payload));
      if (
        createOfferCalculationAsync.rejected.match(result) ||
        result.payload?.errors ||
        !result.payload?.id
      ) {
        toast.error(t("offers.tabs.calculation_load_failed"));
        return;
      }
      const savedCopy = { ...result.payload, items: result.payload.items || [] };
      await attachSavedNestedLine(savedCopy, parent, mode, targetRow);
    },
    [attachSavedNestedLine, dispatch, t]
  );

  const finishBlankNested = useCallback(
    async (parent, mode, targetRow) => {
      const payload = buildBlankNestedCalculationPayload({
        parentId: parent.id,
        offerId: parent.offer_id || null,
        title: t("offer.calculation.nested.blank_title"),
      });
      const result = await dispatch(createOfferCalculationAsync(payload));
      if (
        createOfferCalculationAsync.rejected.match(result) ||
        result.payload?.errors ||
        !result.payload?.id
      ) {
        toast.error(t("offers.tabs.calculation_load_failed"));
        return;
      }
      const savedCopy = { ...result.payload, items: result.payload.items || [] };
      await attachSavedNestedLine(savedCopy, parent, mode, targetRow);
    },
    [attachSavedNestedLine, dispatch, t]
  );

  const handleCalculationSelect = useCallback(
    async (selectedCalculations, modeOverride, rowOverride) => {
      const templateId = selectedCalculations?.[0]?.id;
      if (!templateId || addingFromTemplateRef.current) return;
      addingFromTemplateRef.current = true;
      const mode = modeOverride || templatePickerMode || "line";
      const targetRow = rowOverride !== undefined ? rowOverride : attachTargetRow;
      try {
        const parent = formikRef.current?.values;
        if (!parent?.id) {
          toast.error(t("offers.tabs.calculation_save_failed"));
          return;
        }
        if (mode === "replace") {
          if (
            calculationHasReplaceableContent(parent) &&
            !window.confirm(t("offers.tabs.calculation_replace_template_confirm"))
          ) {
            return;
          }
          const calcData = await fetchOfferCalculation(templateId);
          if (!calcData || calcData.errors || calcData.id == null) {
            toast.error(t("offers.tabs.calculation_load_failed"));
            return;
          }
          setTemplatePickerOpen(false);
          setAttachTargetRow(null);
          if (templateNeedsWizard(calcData, t)) {
            setTemplateWizard({ calcData, mode: "replace", parent });
            return;
          }
          await applyPageTemplate(calcData, parent);
          return;
        }

        const calcData = await fetchOfferCalculation(templateId);
        if (!calcData || calcData.errors || calcData.id == null) {
          toast.error(t("offers.tabs.calculation_load_failed"));
          return;
        }
        setTemplatePickerOpen(false);
        setAttachTargetRow(null);
        if (templateNeedsWizard(calcData, t)) {
          setTemplateWizard({ calcData, mode, parent, targetRow });
          return;
        }
        await finishNestedFromTemplate(calcData, parent, mode, targetRow);
      } catch (error) {
        console.error("Error fetching calculation template:", error);
        toast.error(t("offers.tabs.calculation_load_failed"));
      } finally {
        addingFromTemplateRef.current = false;
      }
    },
    [
      applyPageTemplate,
      attachTargetRow,
      finishNestedFromTemplate,
      formikRef,
      t,
      templatePickerMode,
    ]
  );

  const openNestedSourceChooser = useCallback((mode = "line", row = null) => {
    setAttachTargetRow(row);
    setTemplatePickerMode(mode);
    setSourceChooserOpen(true);
  }, []);

  const handleLoadPageTemplate = useCallback(() => {
    setAttachTargetRow(null);
    setTemplatePickerMode("replace");
    setTemplatePickerOpen(true);
  }, []);

  const handleInsertTemplateAsLine = useCallback(() => {
    openNestedSourceChooser("line", null);
  }, [openNestedSourceChooser]);

  const handleAttachNestedTemplate = useCallback(
    (row, template) => {
      if (template?.id) {
        handleCalculationSelect([template], "attach", row);
        return;
      }
      openNestedSourceChooser("attach", row);
    },
    [handleCalculationSelect, openNestedSourceChooser]
  );

  const handleChooseNestedNew = useCallback(async () => {
    if (sourceChooserBusy || addingFromTemplateRef.current) return;
    addingFromTemplateRef.current = true;
    setSourceChooserBusy(true);
    try {
      const parent = formikRef.current?.values;
      if (!parent?.id) {
        toast.error(t("offers.tabs.calculation_save_failed"));
        return;
      }
      const mode = templatePickerMode === "attach" ? "attach" : "line";
      setSourceChooserOpen(false);
      await finishBlankNested(parent, mode, attachTargetRow);
      setAttachTargetRow(null);
    } finally {
      setSourceChooserBusy(false);
      addingFromTemplateRef.current = false;
    }
  }, [
    attachTargetRow,
    finishBlankNested,
    formikRef,
    sourceChooserBusy,
    t,
    templatePickerMode,
  ]);

  const handleChooseNestedExisting = useCallback(() => {
    setSourceChooserOpen(false);
    setTemplatePickerOpen(true);
  }, []);

  const handleNestedModulePersist = useCallback(
    async (nested) => {
      if (!nested?.id) return null;
      const result = await dispatch(
        updateOfferCalculationAsync({
          id: nested.id,
          title: nested.title,
          description: nested.description,
          module_enabled: nested.module_enabled !== false,
          module_graph: nested.module_graph,
          module_result: nested.module_result,
        })
      );
      if (
        updateOfferCalculationAsync.rejected.match(result) ||
        result.payload?.errors
      ) {
        toast.error(t("offers.tabs.calculation_save_failed"));
        return null;
      }
      return result.payload;
    },
    [dispatch, t]
  );

  const templateDialogs = (
    <>
      <CalculationNestedSourceDialog
        open={sourceChooserOpen}
        busy={sourceChooserBusy}
        onClose={() => {
          if (sourceChooserBusy) return;
          setSourceChooserOpen(false);
          setAttachTargetRow(null);
        }}
        onChooseNew={handleChooseNestedNew}
        onChooseExisting={handleChooseNestedExisting}
      />

      <Dialog
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {templatePickerMode === "replace"
            ? t("offers.tabs.calculation_select_template_replace")
            : templatePickerMode === "attach"
              ? t("offers.tabs.calculation_select_template_attach")
              : t("offers.tabs.calculation_select_template_as_line")}
        </DialogTitle>
        <DialogContent>
          <ModernCalculationSelector
            open={templatePickerOpen}
            excludeCalculationId={formikRef.current?.values?.id}
            selectedCalculations={[]}
            onChange={handleCalculationSelect}
            onClose={() => setTemplatePickerOpen(false)}
            multiple={false}
            showHeader={false}
            createTemplatePath={null}
          />
        </DialogContent>
      </Dialog>

      <OfferTemplateWizardDialog
        open={Boolean(templateWizard)}
        templateCalc={templateWizard?.calcData}
        onClose={() => setTemplateWizard(null)}
        onConfirm={async (answeredCalc) => {
          const ctx = templateWizard;
          setTemplateWizard(null);
          if (!ctx?.parent || !answeredCalc) return;
          if (ctx.mode === "replace") {
            await applyPageTemplate(answeredCalc, ctx.parent);
            return;
          }
          await finishNestedFromTemplate(
            answeredCalc,
            ctx.parent,
            ctx.mode,
            ctx.targetRow
          );
        }}
      />

      <NestedCalculationEditorDialog
        open={nestedEditorId != null}
        calculationId={nestedEditorId}
        initialTab="products"
        focusRecommendedFields={nestedEditorFocusRecommended}
        onClose={() => {
          setNestedEditorId(null);
          setNestedEditorFocusRecommended(false);
        }}
        onSaved={() => {
          const id = formikRef.current?.values?.id;
          if (id) dispatch(getOfferCalculationAsync(id));
        }}
      />
    </>
  );

  return {
    collaborationBlocked: templatePickerOpen || sourceChooserOpen || sourceChooserBusy || !!templateWizard || nestedEditorId != null,
    handleLoadPageTemplate,
    handleInsertTemplateAsLine,
    handleAttachNestedTemplate,
    handleNestedModulePersist,
    setNestedEditorId,
    setNestedEditorFocusRecommended,
    templateDialogs,
  };
}
